import axios from "axios";
import { bearerOf, installFetchSessionGuard, noteAuthResponse, RENEWED_TOKEN_HEADER } from "@/lib/sessionGuard";

/**
 * Runtime session enforcement, on the axios instance every page actually uses.
 *
 * A 401 from any protected route means the bearer token is dead — expired,
 * revoked, or forged in localStorage. The shell must not keep rendering as if
 * the session were alive: sessionGuard ends the session and useSession lands on
 * /login. A renewed token in the answer replaces the stored one. The boot-time
 * GET /auth/me check in useSession covers the initial load; this covers after it.
 *
 * Two endpoints answer 401 by DESIGN and must never trip this:
 *   · /auth/login      — a wrong PIN on the login screen;
 *   · /auth/authorize  — a manager typing a wrong PIN into a comp/discount/
 *                        cancel dialog. Logging the cashier out for that would
 *                        turn a typo into a lost cart.
 *
 * This used to sit on a private axios instance (productionGateway) that no
 * screen ever gave a token to, so it enforced nothing. Installed once, at
 * module load, from useSession.
 */
let installed = false;

const noteAxios = (config, response) => {
  if (!response) return;
  const headers = config?.headers || {};
  const auth = typeof headers.get === "function" ? headers.get("Authorization") : headers.Authorization ?? headers.authorization;
  noteAuthResponse({
    url: config?.url,
    requestToken: bearerOf(auth),
    status: response.status,
    renewedToken: response.headers?.[RENEWED_TOKEN_HEADER.toLowerCase()] || null,
  });
};

export const SERVER_DOWN_MESSAGE = "The server is not answering. Check that the server PC is on and connected.";
const SERVER_DOWN_STATUSES = [502, 503, 504];

export function installAuthInterceptor() {
  if (installed) return;
  installed = true;
  installFetchSessionGuard();
  axios.interceptors.response.use(
    response => {
      noteAxios(response?.config, response);
      return response;
    },
    error => {
      // Every screen that shows `error.message` showed axios's own words
      // ("Request failed with status code 504", "Network Error") when the
      // server PC was off. Say what happened instead; the response, if any,
      // is untouched so status checks still work (owner, 2026-10-25).
      if (error && !axios.isCancel?.(error) && (!error.response || SERVER_DOWN_STATUSES.includes(error.response.status))) {
        error.message = SERVER_DOWN_MESSAGE;
      }
      noteAxios(error?.config, error?.response);
      return Promise.reject(error);
    },
  );
}
